import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useWindowSize } from 'hooks';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

// Format time value for display with mixed units
const formatTime = (minutes, returnUnit = false) => {
  // Handle null, undefined, and invalid values
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
    return returnUnit ? { value: null, unit: null } : null;
  }

  // Negative values shouldn't happen, but treat as invalid if they do
  if (minutes < 0) {
    return returnUnit ? { value: null, unit: null } : null;
  }

  // Show "< 1" for values under 1 minute (imprecise timestamps)
  if (minutes < 1) {
    return returnUnit ? { value: '< 1', unit: 'min' } : '< 1 min';
  }

  // Use minutes for values < 60, hours for values >= 60
  if (minutes < 60) {
    const value = Math.round(minutes);
    return returnUnit ? { value, unit: 'min' } : `${value} min`;
  }

  const value = (minutes / 60).toFixed(1);
  return returnUnit ? { value, unit: 'hrs' } : `${value} hrs`;
};

// Custom tooltip to show user response details
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        <TooltipLabel>{label}</TooltipLabel>
        {payload.map((entry, index) => {
          if (entry.value === null || entry.value === undefined) return null;
          // Use raw value from payload if available, otherwise use displayed value
          const rawValue = entry.payload[`${entry.name}_raw`];
          const displayValue =
            rawValue !== null && rawValue !== undefined
              ? rawValue
              : entry.value;
          return (
            <TooltipValue key={index}>
              <UserDot $color={entry.fill} />
              <span style={{ fontWeight: 600 }}>{entry.name}:</span>{' '}
              {formatTime(displayValue)}
            </TooltipValue>
          );
        })}
      </TooltipContainer>
    );
  }
  return null;
}

function UserResponseTimeGroupedBarChart({ data }) {
  const { width } = useWindowSize();

  // State for responsive behavior
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);

  // Track screen width for responsive behavior
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenWidth < 1200;

  // Generate color palette for users
  const userColors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange
    '#8B5CF6', // Purple
    '#EF4444', // Red
    '#06B6D4', // Cyan
    '#F43F5E', // Rose
    '#84CC16', // Lime
    '#A855F7', // Violet
    '#14B8A6' // Teal
  ];

  // Transform data from user-centric to question-centric format
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Extract all questions from first user (all users have same questions)
    const questions = data[0]?.responses || [];

    const MAX_HOURS = 24;
    const MAX_MINUTES = MAX_HOURS * 60; // 1440 minutes

    // Build question-centric data structure
    const allQuestions = questions
      .map((question) => {
        const questionData = {
          questionTitle: question.questionTitle,
          questionOrder: question.questionOrder
        };

        // Add each user's response time for this question
        data.forEach((user) => {
          const response = user.responses.find(
            (r) => r.questionId === question.questionId
          );
          const rawTime = response?.responseTime ?? null;

          // Store both raw time (for tooltip) and capped/clamped time (for chart display)
          if (rawTime !== null) {
            questionData[`${user.userName}_raw`] = rawTime;
            // Clamp to minimum 1 minute for log scale (log can't handle 0 or values <1)
            const clampedTime = Math.max(rawTime, 1);
            questionData[user.userName] = Math.min(clampedTime, MAX_MINUTES);
          } else {
            questionData[`${user.userName}_raw`] = null;
            questionData[user.userName] = null;
          }
        });

        return questionData;
      })
      .sort((a, b) => a.questionOrder - b.questionOrder);

    // On mobile, only show selected question
    if (isMobile && allQuestions.length > 0) {
      return [allQuestions[selectedQuestionIndex] || allQuestions[0]];
    }

    return allQuestions;
  }, [data, isMobile, selectedQuestionIndex]);

  // Extract user names for creating bars
  const userNames = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((user) => user.userName);
  }, [data]);

  // Get list of all questions for dropdown
  const allQuestions = useMemo(() => {
    if (!data || data.length === 0) return [];
    const questions = data[0]?.responses || [];
    return questions
      .map((q) => ({ title: q.questionTitle, order: q.questionOrder }))
      .sort((a, b) => a.order - b.order);
  }, [data]);

  // Calculate dynamic bar spacing based on question count
  const barCategoryGap = useMemo(() => {
    if (isMobile) return '10%';

    const questionCount = allQuestions.length;
    if (questionCount <= 5) return '10%';
    if (questionCount <= 10) return '15%';
    return 25; // Fixed pixel value for many questions
  }, [isMobile, allQuestions.length]);

  // Calculate average response time across all users
  const averageResponseTime = useMemo(() => {
    if (!data || data.length === 0) return null;

    const allResponseTimes = [];
    data.forEach((user) => {
      user.responses.forEach((response) => {
        if (
          response.responseTime !== null &&
          response.responseTime !== undefined
        ) {
          allResponseTimes.push(response.responseTime);
        }
      });
    });

    if (allResponseTimes.length === 0) return null;

    const sum = allResponseTimes.reduce((acc, time) => acc + time, 0);
    return sum / allResponseTimes.length;
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No user response time data available</EmptyMessage>
      </EmptyState>
    );
  }

  if (!chartData) {
    return (
      <EmptyState>
        <EmptyMessage>Unable to generate chart</EmptyMessage>
      </EmptyState>
    );
  }

  return (
    <Container>
      <Description>
        <DescriptionText>
          Each question shows grouped bars representing each user&apos;s
          response time. Compare users side-by-side to identify patterns and
          outliers.
        </DescriptionText>
      </Description>

      {isMobile && allQuestions.length > 0 && (
        <QuestionSelector>
          <QuestionLabel>Select Question:</QuestionLabel>
          <QuestionSelect
            value={selectedQuestionIndex}
            onChange={(e) => setSelectedQuestionIndex(Number(e.target.value))}>
            {allQuestions.map((question, index) => (
              <option key={index} value={index}>
                {question.title}
              </option>
            ))}
          </QuestionSelect>
        </QuestionSelector>
      )}

      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20
          }}
          barCategoryGap={barCategoryGap}
          barGap={4}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="questionTitle"
            tick={{ fontSize: 12, fill: '#666', dy: 0 }}
            angle={-45}
            textAnchor="end"
            height={45}
          />
          <YAxis
            scale="log"
            domain={[1, 1440]}
            ticks={[1, 5, 15, 30, 60, 180, 360, 720, 1440]}
            tick={{ fontSize: 12, fill: '#666' }}
            label={{
              value: 'Response Time (log scale)',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
            }}
            tickFormatter={(value) => {
              if (value < 60) {
                return `${value}m`;
              }
              const hours = value / 60;
              if (hours >= 24) return '24h+';
              return `${hours}h`;
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            // height={36}
            // wrapperStyle={{ paddingBottom: '8px' }}
            formatter={(value) => <StyledLegendText>{value}</StyledLegendText>}
          />

          {/* Create a bar for each user */}
          {userNames.map((userName, index) => (
            <Bar
              key={userName}
              dataKey={userName}
              fill={userColors[index % userColors.length]}
              radius={[4, 4, 0, 0]}
              maxBarSize={60}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>

      {averageResponseTime !== null && (
        <StatsBox>
          <StatsLabel>Average Response Time:</StatsLabel>
          <StatsValue>{formatTime(averageResponseTime)}</StatsValue>
        </StatsBox>
      )}

      <LegendBox>
        <LegendTitle>Reading the Chart:</LegendTitle>
        {width < 1200 ? (
          <LegendNote>
            Bars show individual user response times. Missing bars indicate the
            user didn&apos;t respond to that question. Lower bars indicate
            faster engagement.
          </LegendNote>
        ) : (
          <LegendNote>
            Each cluster of bars represents a question. Within each cluster,
            bars show individual user response times. Missing bars indicate the
            user didn&apos;t respond to that question. Lower bars indicate
            faster engagement.
          </LegendNote>
        )}
      </LegendBox>
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem 0 0;
  width: 100%;

  .recharts-legend-wrapper {
    top: 0 !important;
  }

  .recharts-default-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.25rem;
    justify-content: center;
  }

  .recharts-legend-item {
    /* margin-bottom: 1.75rem; */
    margin-right: 0 !important;
  }
`;

const Description = styled.div`
  margin-bottom: 1.875rem;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const DescriptionText = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

const QuestionSelector = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1.625rem;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const QuestionLabel = styled.label`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
`;

const QuestionSelect = styled.select`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: var(--brown);
  cursor: pointer;
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  outline: none;
  transition: border-color 0.2s;
  max-width: 120px;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f9f9f9;
  border-radius: 8px;
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin: 0;
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TooltipValue = styled.div`
  align-items: center;
  color: var(--light-brown);
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  gap: 0.5rem;
  margin: 0.25rem 0;
`;

const UserDot = styled.div`
  background-color: ${(props) => props.$color};
  border-radius: 50%;
  height: 8px;
  width: 8px;
`;

const StyledLegendText = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

const StatsBox = styled.div`
  align-items: center;
  background: #f0f9ff;
  border-radius: 6px;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 1.5rem;
  padding: 0.875rem 1.5rem;
`;

const StatsLabel = styled.span`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
`;

const StatsValue = styled.span`
  color: #0284c7;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 700;
`;

const LegendBox = styled.div`
  background: #f9fafb;
  border-radius: 6px;
  margin-top: 1.5rem;
  padding: 1rem 1.5rem;
`;

const LegendTitle = styled.h4`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const LegendNote = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  line-height: 1.4;
  margin: 0;
`;

export default UserResponseTimeGroupedBarChart;
